import { toast } from 'react-toastify';
import { JAVA_API_STATUS } from '../../../utils/axios-interceptor';
import { downloadBlobFromUrl } from '../../../utils/downloadUtils';
import { fetchBankStatementDataService, fetchBankStatementRowTransactionsService, bsaGenerateService } from './service';
import { BSA_REPORT_DOWNLOAD_FAILED_MESSAGE, BSA_REPORT_GENERATION_FAILED_MESSAGE } from './constants';
import {
    BankStatementDataHandlers,
    BankStatementRangePayload,
    BankStatementRowTransactionsHandlers,
    BankStatementRowTransactionsPayload,
    BsaReportGenerateData,
    BsaGenerateHandlers,
    BsaHandlers,
    BsaReportWindow,
} from './types';

export async function fetchBankStatementData(payload: BankStatementRangePayload, handlers: BankStatementDataHandlers) {
    try {
        const response = await fetchBankStatementDataService(payload);
        const responseData = response.data?.responseData;
        switch (responseData?.responseCode) {
            case JAVA_API_STATUS.SUCCESS:
                handlers?.onSuccess(response.data?.bankData || {});
                return;
            case JAVA_API_STATUS.ERROR:
                handlers?.onError?.();
                return toast.error(responseData?.responseMessage || 'Some error occoured');
        }
    } catch (err) {
        handlers?.onError?.();
        console.error('Error in fetching bank statement data', err);
    }
}

export async function fetchBankStatementRowTransactions(
    orgId: string,
    payload: BankStatementRowTransactionsPayload,
    handlers: BankStatementRowTransactionsHandlers,
) {
    try {
        const response = await fetchBankStatementRowTransactionsService(orgId, payload);
        const rows = response?.data?.responseBody?.data;
        if (Array.isArray(rows)) {
            handlers.onSuccess(rows);
        } else {
            handlers.onError();
        }
    } catch (err) {
        handlers.onError();
        console.error('Error in fetching bank statement row transactions', err);
    }
}

// Single blocking call: generates the report and hands the presigned URL back
// to the caller (which opens it in the in-app viewer) — UW only responds once
// the file is generated and uploaded, so there is no separate status/download
// round trip to make.
export async function generateBsaReport(investeeOrgId: string, handlers?: BsaGenerateHandlers, reportWindow?: BsaReportWindow) {
    try {
        const response = await bsaGenerateService(investeeOrgId, reportWindow);
        const data: BsaReportGenerateData = response?.data?.data;
        // Require both fields: the viewer opens on presignedUrl, but the drawer's
        // Download action needs fileName — treat a partial response as a failure
        // rather than opening a viewer whose Download button silently no-ops.
        if (!data?.presignedUrl || !data?.fileName) {
            handlers?.onError?.();
            toast.error(BSA_REPORT_GENERATION_FAILED_MESSAGE);
            return;
        }
        handlers?.onSuccess?.(data);
    } catch (err: any) {
        handlers?.onError?.();
        console.error('Error in generating bank statement analysis report', err);
        // Surface the server's message only for 4xx business rejections (e.g. D7's
        // window-too-wide 400) — a 5xx/gateway `message` may carry internals and must not
        // reach the toast (PR review).
        const status = err?.response?.status;
        const beMessage = err?.response?.data?.message;
        const isBusinessRejection = typeof status === 'number' && status >= 400 && status < 500;
        toast.error(isBusinessRejection && typeof beMessage === 'string' && beMessage ? beMessage : BSA_REPORT_GENERATION_FAILED_MESSAGE);
    }
}

// Downloads a report directly from a presigned S3 URL: plain fetch (no auth headers) —
// mirrors the established presigned-download pattern (UploadCard/utils/api/helpers.ts downloadFile),
// not axios, since the interceptor attaches x-cerberus-token to every axios request by default.
export async function downloadBsaReportFile(presignedUrl: string, fileName: string, handlers?: BsaHandlers) {
    try {
        const res = await fetch(presignedUrl);
        if (res.status !== 200) {
            handlers?.onError?.();
            toast.error(BSA_REPORT_DOWNLOAD_FAILED_MESSAGE);
            return;
        }
        const blob = await res.blob();
        downloadBlobFromUrl(blob, fileName);

        handlers?.onSuccess?.();
        toast.success('Bank Statement Analysis Report downloaded successfully');
    } catch (err) {
        handlers?.onError?.();
        console.error('Error in downloading bank statement analysis report', err);
        toast.error(BSA_REPORT_DOWNLOAD_FAILED_MESSAGE);
    }
}
